import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, User, Shield, AlertTriangle, CheckCircle, Edit, Eye, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface User {
  id: string
  name: string
  email: string
  phone: string
  type: 'customer' | 'store_owner' | 'store_admin' | 'dispatcher' | 'chef' | 'delivery' | 'platform_admin'
  is_active: boolean
  email_verified: boolean
  created_at: string
  store_id?: string
}

const AdminUserManagement = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const usersPerPage = 10

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async (page = 1) => {
    const cacheKey = `admin_users_${page}`
    
    // Intentar cargar desde cache primero
    const cachedUsers = getCacheStale<User[]>(cacheKey)
    if (cachedUsers && cachedUsers.length > 0) {
      setUsers(cachedUsers)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/admin/users?page=${page}&limit=${usersPerPage}`)
      const usersData = response.data.data || []
      setUsers(usersData)
      setTotalPages(response.data.pagination?.totalPages || 1)
      setTotalUsers(response.data.pagination?.total || 0)
      setCurrentPage(page)
      
      // Guardar en cache (5 minutos de TTL - usuarios cambian moderadamente)
      setCache(cacheKey, usersData, 5 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading users:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedUsers) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      setUsers([])
      setTotalPages(1)
      setTotalUsers(0)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { isActive })
      setUsers(users.map(user =>
        user.id === userId ? { ...user, is_active: isActive } : user
      ))
      // Reload current page to get updated data
      await loadUsers(currentPage)
    } catch (error) {
      console.error('Error updating user status:', error)
    }
  }



  const filteredUsers = (users || []).filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || user.type === filterType
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'active' && user.is_active) ||
                         (filterStatus === 'inactive' && !user.is_active)

    return matchesSearch && matchesType && matchesStatus
  })

  const getRoleColor = (type: string) => {
    switch (type) {
      case 'platform_admin': return 'bg-red-100 text-red-800'
      case 'store_owner': return 'bg-blue-100 text-blue-800'
      case 'store_admin': return 'bg-purple-100 text-purple-800'
      case 'dispatcher': return 'bg-orange-100 text-orange-800'
      case 'chef': return 'bg-green-100 text-green-800'
      case 'delivery': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (type: string) => {
    switch (type) {
      case 'platform_admin': return 'Admin Plataforma'
      case 'store_owner': return 'Dueño Colmado'
      case 'store_admin': return 'Admin Colmado'
      case 'dispatcher': return 'Despachador'
      case 'chef': return 'Cocinero'
      case 'delivery': return 'Repartidor'
      default: return 'Cliente'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando usuarios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <AdminNavHeader
        title="Gestión de Usuarios"
        subtitle={`${totalUsers} usuarios registrados`}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 safe-area-bottom">
        {/* Stats Cards - Moved to top */}
        <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Total</p>
                <p className="text-lg font-bold text-gray-900">{totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Activos</p>
                <p className="text-lg font-bold text-gray-900">
                  {users.filter(u => u.is_active).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Dueños</p>
                <p className="text-lg font-bold text-gray-900">
                  {users.filter(u => u.type === 'store_owner').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Inactivos</p>
                <p className="text-lg font-bold text-gray-900">
                  {users.filter(u => !u.is_active).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-gray-900 placeholder-gray-500 shadow-sm"
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all shadow-sm"
            >
              <option value="all">Todos los roles</option>
              <option value="customer">Clientes</option>
              <option value="store_owner">Dueños</option>
              <option value="store_admin">Admins</option>
              <option value="dispatcher">Despachadores</option>
              <option value="chef">Cocineros</option>
              <option value="delivery">Repartidores</option>
              <option value="platform_admin">Admins Plataforma</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-xl text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all shadow-sm"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
        </div>

        {/* Users Table - Desktop / Cards - Mobile */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Teléfono</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Registro</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.type)}`}>
                        {getRoleLabel(user.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.phone || 'No especificado'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(user.created_at).toLocaleDateString('es-DO', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {user.is_active ? (
                          <button
                            onClick={() => handleStatusChange(user.id, false)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Suspender"
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(user.id, true)}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                            title="Activar"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet Card View */}
          <div className="lg:hidden">
            <div className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <div key={user.id} className="p-4 hover:bg-gray-50/50 transition-colors">
                  {/* User Header */}
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{user.name}</h3>
                      <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.type)}`}>
                        {getRoleLabel(user.type)}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>

                  {/* User Details */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-gray-500">Teléfono</p>
                      <p className="font-medium text-gray-900">{user.phone || 'No especificado'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Registro</p>
                      <p className="font-medium text-gray-900">
                        {new Date(user.created_at).toLocaleDateString('es-DO', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors active:scale-95"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                    <button
                      onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 rounded-xl text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors active:scale-95"
                    >
                      <Edit className="w-4 h-4" />
                      Editar
                    </button>
                    {user.is_active ? (
                      <button
                        onClick={() => handleStatusChange(user.id, false)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 rounded-xl text-sm font-medium text-red-700 hover:bg-red-100 transition-colors active:scale-95"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        Suspender
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(user.id, true)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-50 rounded-xl text-sm font-medium text-green-700 hover:bg-green-100 transition-colors active:scale-95"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Activar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredUsers.length === 0 && !loading && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron usuarios</h3>
            <p className="text-gray-600">Intenta ajustar los filtros de búsqueda</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center space-x-4">
            <button
              onClick={() => loadUsers(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-2xl text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors active:scale-95 shadow-sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-sm">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => loadUsers(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-2xl text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors active:scale-95 shadow-sm"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminUserManagement
